import type { CompletionFilterData } from '@hotjar/feedback/domain/Surveys/Responses/Filters/FilterData';
import { useCallback, useMemo } from 'react';
import { GenericRadioFilter } from '@hotjar/global-filters';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { CompletionFilterValue } from '@hotjar/feedback/domain/Surveys/Responses/Filters/FilterDataType';
import { CompletionOptions } from '@hotjar/feedback/domain/Surveys/Responses/Filters/FilterDataType';
import type { ChangeFilter, RemoveFilter } from './FilterTypes';

export const CompletionFilter = ({
  filter,
  onChange,
  onRemove,
}: {
  filter: CompletionFilterData;
  onChange: ChangeFilter<CompletionFilterData>;
  onRemove: RemoveFilter<CompletionFilterData>;
}): JSX.Element => {
  const onApply = useCallback(
    (newValue: CompletionFilterValue) => {
      onChange(filter, newValue);
    },
    [onChange, filter],
  );

  const onRemoveFilter = useCallback(() => {
    onRemove(filter);
  }, [onRemove, filter]);

  const { t } = useTranslationWithPrefix('feedback:survey-response.filters.completion');

  const options = useMemo(() => {
    return [
      {
        value: CompletionOptions.ALL,
        label: t('options.all'),
      },
      {
        value: CompletionOptions.COMPLETE,
        label: t('options.complete'),
      },
      {
        value: CompletionOptions.INCOMPLETE,
        label: t('options.incomplete'),
      },
    ];
  }, [t]);

  const label2 = useMemo(() => {
    switch (filter.value) {
      case 'complete':
        return t('active.value-is-complete');
      case 'incomplete':
        return t('active.value-is-incomplete');
      default:
        return undefined;
    }
  }, [t, filter.value]);

  const isDefault = filter.value === undefined || filter.value === CompletionOptions.ALL;

  return (
    <GenericRadioFilter<CompletionFilterValue>
      currentValue={filter.value}
      optionList={options}
      filterName={t('filter-name')}
      iconName="boolean"
      onApply={onApply}
      onRemove={!isDefault ? onRemoveFilter : undefined}
      isInactive={isDefault}
      label1={!isDefault ? t('response') : undefined}
      label2={label2}
      showDropdownCaret
    />
  );
};
